import { Component, ElementRef, Injector, NgZone, ViewChild, OnInit, AfterViewInit } from '@angular/core';
import { Router } from '@angular/router';

// Rxjs
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

// Services
import { AuthService } from '@auth/auth.service';
import { WebcomponentsService } from '@core/service/webcomponents.service';
import { MarketingEventEnumeration, MarketingEventService, RecurrentStatesEnumeration } from '@shared/services';

// Enum
import { PRODUCT_ID_ENUMERATION, ProductType } from '@core/model/enums';

// Configs
import { ROUTES_CONSTANTS } from '@shared/configs';

import { CHECK_PROCESS_STATE_ENUMERATION, checkProcessRedirect } from '@core/configs/check-process';

@Component({
  selector: 'app-references',
  templateUrl: './references.component.html',
})
export class ReferencesComponent implements OnInit, AfterViewInit {
  @ViewChild('referencesRef') public referencesRef!: ElementRef;

  private readonly webElementService: WebcomponentsService;
  private readonly ngZone: NgZone;
  private readonly router: Router;
  private readonly authService: AuthService;
  private readonly marketingEventService: MarketingEventService;
  public clientType: RecurrentStatesEnumeration;
  public productId: number;
  private unSubscribe$: Subject<void>;

  public isReferencesLoading: boolean;

  constructor(private readonly injector: Injector) {
    this.webElementService = this.injector.get(WebcomponentsService);
    this.ngZone = this.injector.get(NgZone);
    this.router = this.injector.get(Router);
    this.authService = this.injector.get(AuthService);
    this.marketingEventService = this.injector.get(MarketingEventService);
    this.clientType = RecurrentStatesEnumeration.IS_NEW;
    this.unSubscribe$ = new Subject<void>();
    this.isReferencesLoading = false;
  }

  public ngOnInit(): void {
    this.isReferencesLoading = true;
  }

  public ngAfterViewInit(): void {
    this.webElementService.isLoaded$.pipe(filter(Boolean), takeUntil(this.unSubscribe$)).subscribe({
      next: () => {
        this.renderReferencesWidget();
      },
    });
  }

  public ngOnDestroy(): void {
    this.unSubscribe$.next();
    this.unSubscribe$.complete();
  }

  private renderReferencesWidget(): void {
    const references = this.referencesRef.nativeElement;

    references.ngElementStrategy?.componentRef?.instance?.build({
      email: this.authService.user.email,
      clientType: this.clientType,
      redirectToFn: this.onConfirm.bind(this),
      alternativeNavigationFn: this.alternativeNavigation.bind(this),
    });

    setTimeout(() => {
      this.isReferencesLoading = false;
    }, 0);
  }

  public onConfirm(): void {
    this.ngZone.run(() => {
      let eventName: MarketingEventEnumeration;
      let productType = this.productId === Number(PRODUCT_ID_ENUMERATION.RP) ? ProductType.RP : ProductType.RFYA;

      if (productType === ProductType.RP) {
        eventName =
          this.clientType === RecurrentStatesEnumeration.IS_RECURRING
            ? MarketingEventEnumeration.RECURRENT_APPROVED_RP
            : MarketingEventEnumeration.NEW_APPROVED_RP;
      } else {
        eventName = MarketingEventEnumeration.LOAN_APPLICATION_SIGNATURE_RFYA;
      }

      this.marketingEventService.sendMarketingEvents(eventName);
      this.router.navigate([ROUTES_CONSTANTS.SIGN_APPLICATION]);
    });
  }

  private alternativeNavigation({ state }: { state: CHECK_PROCESS_STATE_ENUMERATION }): void {
    this.ngZone.run(() => {
      const route = checkProcessRedirect(state, [CHECK_PROCESS_STATE_ENUMERATION.PENDING_REFERENCES]);
      if (route) {
        this.router.navigate([route]);
      }
    });
  }
}
